import i18n from '../../i18n';
import { ISelectOptions } from './common';

/**
 * Career profile view-model types (spec 0018, increment B).
 *
 * These describe the shapes the LMS profile form works with. The GraphQL <->
 * form bridging (int year/month <-> string, technologyIds <-> chips) lives in the
 * `core/hooks/CareerProfile/transform` layer.
 */

/** A dictionary skill the student has picked, with a 1-5 proficiency level. */
export interface ICareerSkillValue {
  skillId: string;
  name: string;
  /** Слаг справочника — по нему витрина подбирает знак технологии. */
  slug: string;
  /** Своя картинка из ERP; `null` — знак по слагу либо буквенная плашка. */
  iconUrl: string | null;
  category: string;
  level: number;
  years: number | null;
  /**
   * ТЗ 0039. 'course' = «подтверждено школой»: навык начислен за пройденный
   * курс, и поставить его себе сам студент не может — поля source нет во
   * входе мутации. Уровень править можно: это его собственная оценка.
   */
  source: CareerSkillSource;
  /** Скрыт из резюме. Подтверждённый навык можно скрыть, но не удалить. */
  isHidden: boolean;
  /** Название курса, который подтвердил навык. Пусто у самозаявленных. */
  grantedCourseName: string | null;
  /** ТЗ 0040: занятие, на котором навык открылся. */
  grantedLessonNumber: number | null;
}

export type CareerSkillSource = 'self' | 'course';

export const CAREER_SKILL_SOURCE_COURSE: CareerSkillSource = 'course';
export const CAREER_SKILL_SOURCE_SELF: CareerSkillSource = 'self';

/** A technology chip on a portfolio project — a dictionary skill reference. */
export interface ICareerTechnology {
  id: string;
  name: string;
  /** Слаг справочника — по нему подбирается знак технологии. */
  slug: string;
  category: string;
  /** Своя картинка из ERP; `null` — знак по слагу либо буквенная плашка. */
  iconUrl: string | null;
}

/** Top-level portfolio project (spec 0018: a junior with no jobs can still show pet projects). */
export interface ICareerProjectValue {
  id: string;
  title: string;
  description: string;
  role: string;
  repoUrl: string;
  demoUrl: string;
  technologies: ICareerTechnology[];
}

export interface ICareerCertificateValue {
  /** Stable client key for the form row (server id for existing rows, nanoid for new). */
  id: string;
  /**
   * The backend certificate id, or `null` for a not-yet-saved row. Certificates are
   * reconciled by id on save: a row WITH `serverId` is updated in place (preserving
   * an admin-uploaded file); a row WITHOUT one is created (spec 0018 §9).
   */
  serverId: string | null;
  title: string;
  issuer: string;
  issuedAt: string;
  url: string;
  fileUrl: string | null;
}

/** Per-language CV headline + about text (RUS / ENG / POL). */
export interface ICvLocale {
  language: string;
  jobTitle: string;
  about: string;
}

export enum WorkMode {
  onsite = 'onsite',
  hybrid = 'hybrid',
  remote = 'remote',
}

export const WorkModeOptions: ISelectOptions[] = [
  { value: WorkMode.onsite, label: `${i18n.t('careerProfile.workMode.onsite')}` },
  { value: WorkMode.hybrid, label: `${i18n.t('careerProfile.workMode.hybrid')}` },
  { value: WorkMode.remote, label: `${i18n.t('careerProfile.workMode.remote')}` },
];

export enum Seniority {
  intern = 'intern',
  junior = 'junior',
  middle = 'middle',
  senior = 'senior',
}

export const SeniorityOptions: ISelectOptions[] = [
  { value: Seniority.intern, label: `${i18n.t('careerProfile.seniority.intern')}` },
  { value: Seniority.junior, label: `${i18n.t('careerProfile.seniority.junior')}` },
  { value: Seniority.middle, label: `${i18n.t('careerProfile.seniority.middle')}` },
  { value: Seniority.senior, label: `${i18n.t('careerProfile.seniority.senior')}` },
];

/** Ordered set of CV languages the picker + download offer. */
export const CV_LANGUAGE_ORDER = ['POL', 'ENG'] as const;

/**
 * Employment type and feed sort (spec 0035).
 *
 * Salary is deliberately NOT a sort option: `salaryMin`/`salaryMax` are both
 * nullable and empty on most agent-filed rows, so a plain salary sort either
 * buries or floats a silent majority. Relevance + newest carries the feature.
 */
export const EmploymentTypeOptions: ISelectOptions[] = [
  { value: 'full_time', label: `${i18n.t('careerCenter.vacancies.employment.full_time')}` },
  { value: 'part_time', label: `${i18n.t('careerCenter.vacancies.employment.part_time')}` },
  { value: 'internship', label: `${i18n.t('careerCenter.vacancies.employment.internship')}` },
  { value: 'contract', label: `${i18n.t('careerCenter.vacancies.employment.contract')}` },
];

export const SortOptions: ISelectOptions[] = [
  { value: 'relevance', label: `${i18n.t('careerCenter.vacancies.sort.relevance')}` },
  { value: 'newest', label: `${i18n.t('careerCenter.vacancies.sort.newest')}` },
];
